import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useRef, type PointerEvent } from "react";

import { SIDEBAR_SHORTCUT } from "@/core/layout/use-sidebar";
import { Tip } from "@/ui/tooltip";

/** How far the edge must be dragged before the sidebar snaps to the other state. */
const SNAP_PX = 48;

/**
 * The sidebar's right edge: a round button (shown while the pointer is over the sidebar or anything in it has
 * keyboard focus; always on touch screens, which can't hover) and the edge line, which can be dragged to collapse or expand.
 * Hidden, the button takes no clicks, so a click near the edge from the page can't hit it unseen.
 * Dragging snaps between the two widths instead of leaving any width, so the layout is always one of two
 * known, tested states. Put inside a `group/sidebar` positioned element.
 */
export function SidebarEdge({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const drag = useRef<{ startX: number; collapsed: boolean } | null>(null);
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, collapsed };
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state) return;
    // Only while the button is held: a release the page never heard (outside the window, a lost focus) mustn't
    // leave a drag running that a plain mouse move over the edge would later finish.
    if ((event.buttons & 1) === 0) {
      drag.current = null;
      return;
    }
    const dx = event.clientX - state.startX;
    // Snap as soon as the drag goes far enough; dragging back the other way snaps back.
    if ((!state.collapsed && dx < -SNAP_PX) || (state.collapsed && dx > SNAP_PX)) {
      onToggle();
      drag.current = { startX: event.clientX, collapsed: !state.collapsed };
    }
  };
  const onPointerEnd = () => {
    drag.current = null;
  };

  return (
    <>
      <div
        aria-hidden="true"
        title={`Drag to ${collapsed ? "expand" : "collapse"}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onPointerEnd}
        className="absolute inset-y-0 -right-1 z-[5] w-2 cursor-ew-resize touch-none after:absolute after:inset-y-0 after:right-[3px] after:w-0.5 after:bg-primary after:opacity-0 after:transition-opacity hover:after:opacity-100"
      />
      <Tip label={`${label} (${SIDEBAR_SHORTCUT})`}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={label}
          aria-expanded={!collapsed}
          aria-controls="sidebar"
          className="absolute top-[4.5rem] -right-3.5 z-10 flex size-7 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm transition-[color,border-color,opacity] hover:border-primary hover:text-primary pointer-fine:pointer-events-none pointer-fine:opacity-0 pointer-fine:group-hover/sidebar:pointer-events-auto pointer-fine:group-hover/sidebar:opacity-100 pointer-fine:group-has-[:focus-visible]/sidebar:pointer-events-auto pointer-fine:group-has-[:focus-visible]/sidebar:opacity-100"
        >
          {collapsed ? (
            <ChevronsRight className="size-4" aria-hidden="true" />
          ) : (
            <ChevronsLeft className="size-4" aria-hidden="true" />
          )}
        </button>
      </Tip>
    </>
  );
}
